import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import NewNavbar from "../components/NewNavbar";
import ProductCard from "../components/ProductCard";
import BoardNameModal from "../components/BoardNameModal";
import FloatingChatBar from "../components/FloatingChatBar";
import { useMarketplaceChat } from "../context/MarketplaceChatProvider.js";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import AuthModal from "../components/AuthModal";
import Footer from "../components/Footer.js";
import RootPersonalizationModal from "../components/RootPersonalizedModal";
import { ReactComponent as ItemNotFoundIcon } from "./../assets/svgs/item-not-found.svg";
import {
  useDeletePinWithBodyMutation,
  useGetPinsCachedQuery,
  useGetViewBoardsQuery,
  usePostEditBoardMutation,
  useProfileReadQuery,
} from "../store/api.ts";

const productHeightClasses = [
  "h-[335px] md:h-[360px]",
  "h-[455px] md:h-[470px]",
  "h-[390px] md:h-[415px]",
  "h-[520px] md:h-[540px]",
  "h-[365px] md:h-[390px]",
  "h-[450px] md:h-[465px]",
];

const getApiErrorMessage = (error, fallbackMessage) => {
  return (
    error?.data?.detail ||
    error?.data?.message ||
    error?.data?.error ||
    error?.error ||
    fallbackMessage
  );
};

const getApiErrorStatus = (error) => {
  const status =
    error?.status ??
    error?.originalStatus ??
    error?.data?.status;

  const parsedStatus = Number(status);

  return Number.isFinite(parsedStatus) ? parsedStatus : null;
};

const formatUpdatedLabel = (dateValue) => {
  if (!dateValue) return "Updated recently";

  const updatedDate = new Date(dateValue);

  if (Number.isNaN(updatedDate.getTime())) {
    return "Updated recently";
  }

  const now = new Date();
  const diffMs = now.getTime() - updatedDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "Updated now";
  if (diffMinutes < 60) return `Updated ${diffMinutes}m`;
  if (diffHours < 24) return `Updated ${diffHours}h`;
  if (diffDays < 7) return `Updated ${diffDays}d`;

  return `Updated ${updatedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
};

const normalizeBoardsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.boards)) return data.boards;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizePinsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.pins)) return data.pins;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizePinnedProduct = (pin) => {
  const product = pin?.product || {};

  const rawSlug = String(
    product?.real_slug ||
      product?.slug ||
      product?.product_url ||
      pin?.slug ||
      "",
  ).trim();

  const isMarketplaceProductBySlug = rawSlug.startsWith("mrk-");

  const productType =
    pin?.product_type ||
    product?.product_type ||
    (isMarketplaceProductBySlug ? "marketplace" : "product");

  const isCollectionProduct =
    productType === "product" &&
    !isMarketplaceProductBySlug;

  const rawPrice =
    product?.price !== null &&
    product?.price !== undefined
      ? product.price
      : "";

  const rawDiscountedPrice =
    product?.discounted_price !== null &&
    product?.discounted_price !== undefined
      ? product.discounted_price
      : "";

  const rawDiscount =
    product?.discount !== null &&
    product?.discount !== undefined
      ? Number(product.discount)
      : 0;

  return {
    id: product?.id || pin?.pin_id,
    pinId: pin?.pin_id,
    boardId: pin?.board_id,
    boardName: pin?.board_name,
    boardSlug: pin?.board_slug,
    name: product?.product_name || product?.name || product?.brand || "Product",
    brand:
      product?.brand ||
      (isCollectionProduct ? "Eveness" : "") ||
      product?.product_name ||
      product?.name ||
      "Product",
    price: rawPrice,
    original_price: rawPrice,
    originalPrice: rawPrice,
    discounted_price: isCollectionProduct
      ? rawDiscountedPrice
      : "",
    discountedPrice: isCollectionProduct
      ? rawDiscountedPrice
      : "",
    discount: isCollectionProduct
      ? rawDiscount
      : 0,
    discountPercent: isCollectionProduct
      ? rawDiscount
      : 0,
    image:
      product?.image_url ||
      product?.image ||
      product?.main_images?.[0] ||
      "",
    image_url:
      product?.image_url ||
      product?.image ||
      product?.main_images?.[0] ||
      "",
    slug: rawSlug,
    real_slug: rawSlug,
    product_url: product?.product_url || rawSlug,
    garment_type: product?.garment_type || "",
    garmentType: product?.garment_type || "",
    product_generated_description:
      product?.product_generated_description || "",
    description:
      product?.product_generated_description || "",
    productType,
    product_type: productType,
    is_marketplace: productType === "marketplace",
    matchPercentage:
      pin?.match_percentage !== undefined && pin?.match_percentage !== null
        ? Number(pin.match_percentage)
        : null,
    match_percentage:
      pin?.match_percentage !== undefined &&
      pin?.match_percentage !== null
        ? Number(pin.match_percentage)
        : null,
    created_at: pin?.created_at,
  };
};

const BoardDetailsSkeleton = () => {
  return (
    <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 xl:columns-6">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={index} className="mb-4 break-inside-avoid animate-pulse">
          <div
            className={`rounded-[7px] bg-[#EFEFEF] ${
              productHeightClasses[index % productHeightClasses.length]
            }`}
          />
          <div className="mt-3 flex items-center justify-between">
            <div className="h-3 w-24 rounded bg-[#EFEFEF]" />
            <div className="h-3 w-12 rounded bg-[#EFEFEF]" />
          </div>
        </div>
      ))}
    </div>
  );
};

const BoardDetailsPage = () => {
  const { boardSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeChatModal } = useMarketplaceChat();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editError, setEditError] = useState("");
  const [localBoardName, setLocalBoardName] = useState("");

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [isHomeProfileFlowOpen, setIsHomeProfileFlowOpen] = useState(false);
  const [isRootPersonalizationModalOpen, setIsRootPersonalizationModalOpen] =
    useState(false);
  const [authTick, setAuthTick] = useState(0);

  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isAuthenticated = Boolean(authToken);

  const {
    data: profileData,
    isSuccess: isProfileSuccess,
    isError: isProfileError,
    error: profileError,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const hasCompletedSurvey =
    isAuthenticated && isProfileSuccess && Boolean(profileData);

  const hasSignedInUserWithoutSurvey =
    isAuthenticated &&
    isProfileError &&
    (profileError?.status === 404 || profileError?.originalStatus === 404);

  const shouldShowSurveyPrompt =
    !isAuthenticated || hasSignedInUserWithoutSurvey;
  const canUseRealChat = hasCompletedSurvey;

  const isProfileChecking =
    isAuthenticated && (isProfileLoading || isProfileFetching);

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };

    const handleLoginCompleted = (event) => {
      setAuthTick((prev) => prev + 1);

      const shouldShowPersonalization =
        event?.detail?.shouldShowPersonalization ||
        event?.detail?.profileMissing;

      if (shouldShowPersonalization) {
        setIsAuthModalOpen(false);
        setIsRootPersonalizationModalOpen(true);
      }
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);
    window.addEventListener("eveness-login-completed", handleLoginCompleted);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
      window.removeEventListener(
        "eveness-login-completed",
        handleLoginCompleted,
      );
    };
  }, []);

  const openLoginModal = () => {
    setAuthModalMode("login");
    setIsAuthModalOpen(true);
  };

  const openSurveyGate = () => {
    closeChatModal();

    if (!isAuthenticated) {
      setIsRootPersonalizationModalOpen(true);
      return;
    }

    if (!hasCompletedSurvey) {
      setIsRootPersonalizationModalOpen(true);
    }
  };

  const startSurveyFlow = () => {
    setIsRootPersonalizationModalOpen(false);
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    setIsHomeProfileFlowOpen(true);
  };

  const handleCloseRootPersonalizationModal = () => {
    setIsRootPersonalizationModalOpen(false);
  };

  const handleRootPersonalizationSignIn = () => {
    setIsRootPersonalizationModalOpen(false);
    openLoginModal();
  };

  const handleHomeProfileComplete = async () => {
    setIsHomeProfileFlowOpen(false);
    setIsRootPersonalizationModalOpen(false);
    if (isAuthenticated) {
      await refetchProfile?.();
    }

    setAuthTick((prev) => prev + 1);
    window.dispatchEvent(new Event("eveness-auth-changed"));
  };

  const handleUnlockMatch = () => {
    startSurveyFlow();
  };

  const boardRouteParam = String(boardSlug || "").trim();
  const numericBoardId = Number(boardRouteParam);
  const isNumericBoardRouteParam =
    boardRouteParam !== "" && Number.isFinite(numericBoardId);
  const routeBoardSlug = isNumericBoardRouteParam ? "" : boardRouteParam;
  const {
    data: boardsData,
    isLoading: isBoardsLoading,
    isFetching: isBoardsFetching,
    refetch: refetchBoards,
  } = useGetViewBoardsQuery();


  const [editBoard, { isLoading: isEditingBoard }] = usePostEditBoardMutation();

  const [deletePin, { isLoading: isDeletingPin }] =
    useDeletePinWithBodyMutation();

  const [deletingProductKey, setDeletingProductKey] = useState("");

  const boards = normalizeBoardsResponse(boardsData);

  const boardFromList = useMemo(() => {
    return boards.find((board) => {
      const boardSlug = String(board?.slug || "").trim();

      if (routeBoardSlug) {
        return boardSlug === routeBoardSlug;
      }

      return Number(board?.id) === numericBoardId;
    });
  }, [boards, routeBoardSlug, numericBoardId]);

  const boardFromRouteState = location.state?.board;

  const boardFromRouteStateMatches =
    boardFromRouteState &&
    ((routeBoardSlug &&
      String(boardFromRouteState?.slug || "").trim() === routeBoardSlug) ||
      (!routeBoardSlug && Number(boardFromRouteState?.id) === numericBoardId));

  const board =
    boardFromList || (boardFromRouteStateMatches ? boardFromRouteState : null);

  const pinsRequestBoardSlug = board?.slug || routeBoardSlug || "";
  const {
    data: pinsData,
    isLoading: isPinsLoading,
    isFetching: isPinsFetching,
    isError: isPinsError,
    error: pinsError,
    refetch: refetchPins,
  } = useGetPinsCachedQuery(
    {
      board_slug: pinsRequestBoardSlug,
    },
    {
      skip: !pinsRequestBoardSlug,
    },
  );

  const resolvedBoardSlug =
    pinsRequestBoardSlug || pinsData?.pins?.[0]?.board_slug || "";


  const pins = normalizePinsResponse(pinsData);
  const products = pins.map(normalizePinnedProduct);

  const boardNameFromData =
    board?.name ||
    pins?.[0]?.board_name ||
    (resolvedBoardSlug ? resolvedBoardSlug.replace(/-/g, " ") : "Board");

  useEffect(() => {
    if (boardNameFromData) {
      setLocalBoardName(boardNameFromData);
    }
  }, [boardNameFromData]);

  const boardName = localBoardName || boardNameFromData;

  const itemCount =
    typeof pinsData?.count === "number"
      ? pinsData.count
      : typeof board?.pin_count === "number"
        ? board.pin_count
        : products.length;

  const updatedAt = pinsData?.updated_at || board?.updated_at;
  const pinsErrorStatus = getApiErrorStatus(pinsError);

  const shouldShowBoardNotFound = !boardRouteParam || (isPinsError && pinsErrorStatus === 404);

  const getProductDeletePayload = (product) => {
    const slug =
      product?.real_slug || product?.slug || product?.product_url || "";

    const productType =
      product?.productType ||
      product?.product_type ||
      (product?.is_marketplace === false ? "product" : "marketplace");

    return {
      board_slug: product?.boardSlug || resolvedBoardSlug,
      slug: String(slug || "").trim(),
      product_type: productType,
    };
  };

  const handleRemoveBoardProduct = async (product) => {
    const deletePayload = getProductDeletePayload(product);

    if (!deletePayload.board_slug) {
      toast.error("Board slug is not available.");
      return;
    }

    if (!deletePayload.slug) {
      toast.error("Product slug is not available.");
      return;
    }

    const productKey = String(
      product?.pinId || product?.id || deletePayload.slug,
    );

    try {
      setDeletingProductKey(productKey);

      const response = await deletePin(deletePayload).unwrap();

      toast.success(response?.detail || "Product removed from board.");

      await Promise.all([
        refetchPins(),
        refetchBoards(),
      ]);

      await refetchBoards();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to remove product."));
    } finally {
      setDeletingProductKey("");
    }
  };

  const handleEditBoard = async (name) => {
    setEditError("");

    try {
      if (!resolvedBoardSlug) {
        setEditError("Board slug is not available.");
        return;
      }

      const updatedBoard = await editBoard({
        boardRename: {
          board_slug: resolvedBoardSlug,
          name,
        },
      }).unwrap();

      setLocalBoardName(updatedBoard?.name || name);

      toast.success("Board updated successfully.");

      setEditModalOpen(false);

      await refetchBoards();

      if (updatedBoard?.slug && updatedBoard.slug !== resolvedBoardSlug) {
        navigate(`/board/${updatedBoard.slug}`, {
          replace: true,
          state: {
            board: {
              ...board,
              ...updatedBoard,
            },
          },
        });
      }
    } catch (error) {
      setEditError(getApiErrorMessage(error, "Failed to update board."));
    }
  };

  if (shouldShowBoardNotFound) {
    return (
      <main className="min-h-screen bg-white pb-[130px]">
        <NewNavbar />

        <RootPersonalizationModal
          isOpen={isRootPersonalizationModalOpen}
          variant={isAuthenticated ? "survey" : "guest"}
          onClose={handleCloseRootPersonalizationModal}
          onSignIn={handleRootPersonalizationSignIn}
          onUnlockMatch={handleUnlockMatch}
        />

        <AuthModal
          isOpen={isAuthModalOpen}
          initialMode={authModalMode}
          onClose={() => setIsAuthModalOpen(false)}
        />

        <HomeProfilePopupFlow
          isOpen={isHomeProfileFlowOpen}
          mode="restart"
          onClose={() => setIsHomeProfileFlowOpen(false)}
          onRestartComplete={handleHomeProfileComplete}
          isAuthenticated={isAuthenticated}
        />
        <section className="flex min-h-[calc(100vh-160px)] items-center justify-center px-5 py-16">
          <div className="mx-auto w-full max-w-[520px] text-center">
            <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
              <ItemNotFoundIcon/>
            </div>
            <h1 className="mt-6 font-inter text-[28px] font-semibold text-[#0F0F0F] sm:text-[34px]">
              Board not found
            </h1>

            <p className="mx-auto mt-3 max-w-[420px] font-inter text-sm leading-6 text-[#80808C] sm:text-base">
              This board may have been removed, is no longer available, or the board
              link may be incorrect.
            </p>

            <Link
              to="/board"
              replace
              className="mt-7 inline-flex h-11 items-center justify-center rounded-[6px] bg-[#252525] px-6 font-inter text-sm font-medium text-white transition hover:bg-[#3A3A3A] focus:outline-none focus:ring-2 focus:ring-[#252525] focus:ring-offset-2"
            >
              Back to boards
            </Link>
          </div>
        </section>

        <FloatingChatBar
          onStartSurvey={openSurveyGate}
          onRequireProfile={openSurveyGate}
          canUseRealChat={canUseRealChat}
          shouldShowSurveyPrompt={shouldShowSurveyPrompt}
          isProfileChecking={isProfileChecking}
          isAuthenticated={isAuthenticated}
        />
      </main>
    );
  }

  return (
    <>
      <main className="min-h-screen bg-white pb-[130px]">
        <NewNavbar />

        <RootPersonalizationModal
          isOpen={isRootPersonalizationModalOpen}
          variant={isAuthenticated ? "survey" : "guest"}
          onClose={handleCloseRootPersonalizationModal}
          onSignIn={handleRootPersonalizationSignIn}
          onUnlockMatch={handleUnlockMatch}
        />

        <AuthModal
          isOpen={isAuthModalOpen}
          initialMode={authModalMode}
          onClose={() => setIsAuthModalOpen(false)}
        />

        <HomeProfilePopupFlow
          isOpen={isHomeProfileFlowOpen}
          mode="restart"
          onClose={() => setIsHomeProfileFlowOpen(false)}
          onRestartComplete={handleHomeProfileComplete}
          isAuthenticated={isAuthenticated}
        />

        <section className="mx-auto w-full max-w-[1680px] px-4 pt-8 md:px-10">
          <div className="mb-9 flex items-start justify-between gap-5">
            <div>
              <button
                type="button"
                onClick={() => navigate("/board")}
                className="mb-[14px] font-spaceMono text-[12px] uppercase tracking-[0.14em] text-[#5C5C5C] hover:text-black"
              >
                ← Boards
              </button>

              <h1 className="font-inter text-[34px] font-medium leading-none text-[#1E1E1E] md:text-[48px]">
                {boardName}
              </h1>

              <div className="mt-6 flex items-center gap-4 font-spaceMono text-[10px] uppercase tracking-[0.12em] text-black">
                <span className="font-bold text-[#1E1E1E]">{itemCount} items</span>
                <span className="text-[#5C5C5C]">·</span>
                <span className="text-[#5C5C5C] font-normal">{formatUpdatedLabel(updatedAt)}</span>
                <span className="text-[#5C5C5C]">·</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setEditError("");
                setEditModalOpen(true);
              }}
              className="
              mt-5 h-[40px] rounded-[5px] border border-[#D9D5D0] bg-white
              px-7 font-inter text-[13px] text-[#1E1E1E] transition font-medium
              hover:bg-[#F6F6F6]
            "
            >
              Edit
            </button>
          </div>

          {isPinsError ? (
            <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] px-5 py-10 text-center">
              <h2 className="font-inter text-[18px] font-medium text-black">
                Failed to load board products
              </h2>

              <p className="mt-2 font-inter text-[14px] text-[#666]">
                {pinsError?.data?.detail || "Please try again."}
              </p>

              <button
                type="button"
                onClick={refetchPins}
                className="mt-5 h-[38px] rounded-[5px] bg-[#313131] px-5 font-inter text-[13px] text-white"
              >
                Retry
              </button>
            </div>
          ) : isPinsLoading || isPinsFetching ? (
            <BoardDetailsSkeleton />
          ) : products.length === 0 ? (
            <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] px-5 py-14 text-center">
              <h2 className="font-inter text-[20px] font-medium text-black">
                No products in this board yet
              </h2>

              <p className="mt-2 font-inter text-[14px] text-[#666]">
                Products you save to this board will appear here.
              </p>
            </div>
          ) : (
            <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 xl:columns-6">
              {products.map((product, index) => (
                <ProductCard
                  key={product.pinId || product.id}
                  item={product}
                  matchPercentage={product.matchPercentage}
                  imageHeightClass={
                    productHeightClasses[index % productHeightClasses.length]
                  }
                  compactActions
                  boardActionMode="remove"
                  onRemoveBoard={handleRemoveBoardProduct}
                  isRemovingBoard={
                    isDeletingPin &&
                    deletingProductKey ===
                      String(product?.pinId || product?.id || product?.slug)
                  }
                />
              ))}
            </div>
          )}
        </section>

        <BoardNameModal
          isOpen={editModalOpen}
          mode="edit"
          initialName={boardName}
          isSubmitting={isEditingBoard}
          errorMessage={editError}
          onClose={() => {
            if (isEditingBoard) return;
            setEditModalOpen(false);
            setEditError("");
          }}
          onSubmit={handleEditBoard}
        />
        <FloatingChatBar
          onStartSurvey={openSurveyGate}
          onRequireProfile={openSurveyGate}
          canUseRealChat={canUseRealChat}
          shouldShowSurveyPrompt={shouldShowSurveyPrompt}
          isProfileChecking={isProfileChecking}
          isAuthenticated={isAuthenticated}
        />
      </main>
      <Footer/>
    </>
  );
};

export default BoardDetailsPage;
